import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MapPin, Navigation, Check, Loader2 } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface LocationSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const POPULAR_LOCATIONS = [
  {
    name: "New Delhi",
    label: "New Delhi, Delhi",
    area: "Connaught Place",
    lat: 28.6139,
    lng: 77.2090,
  },
  {
    name: "Patna",
    label: "Patna, Bihar",
    area: "Patna",
    lat: 25.5941,
    lng: 85.1376,
  },
];

export const LocationSelectorModal: React.FC<LocationSelectorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { coords, setManualLocation } = useLocation();
  const [retrying, setRetrying] = React.useState(false);

  const handleSelect = (label: string, area: string, lat: number, lng: number) => {
    setManualLocation(label, area, lat, lng);
    onClose();
  };

  const handleDetect = () => {
    if (!navigator.geolocation) return;
    setRetrying(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
            { headers: { "Accept-Language": "en" } }
          );
          if (res.ok) {
            const data = await res.json();
            const addr = data.address || {};
            const area =
              addr.suburb ||
              addr.neighbourhood ||
              addr.city_district ||
              addr.town ||
              addr.city ||
              addr.county ||
              "Unknown";
            const city = addr.city || addr.town || addr.state_district || addr.state || "";
            const label = city && area !== city ? `${area}, ${city}` : area;
            setManualLocation(label, area, latitude, longitude);
          } else {
            setManualLocation(`Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`, "Detected", latitude, longitude);
          }
        } catch {
          setManualLocation(`Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`, "Detected", latitude, longitude);
        }
        setRetrying(false);
        onClose();
      },
      (error) => {
        console.error("Geolocation error:", error);
        setRetrying(false);
        alert("Could not detect location. Please select a city manually or check your browser permissions.");
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] rounded-2xl z-50">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Select Delivery Location
          </DialogTitle>
          <DialogDescription>
            Choose your city to see nearby stores and products.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <Button
            variant="outline"
            className="w-full flex items-center justify-start gap-3 h-12 rounded-xl text-sm border-dashed hover:border-primary hover:text-primary transition-all"
            onClick={handleDetect}
            disabled={retrying}
          >
            {retrying ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <Navigation className="h-4 w-4 text-primary" />
            )}
            <div className="text-left">
              <p className="font-semibold">Detect my current location</p>
              <p className="text-xs text-muted-foreground">Using GPS / Network</p>
            </div>
          </Button>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground font-semibold">Or Select City</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {POPULAR_LOCATIONS.map((loc) => {
              const isSelected =
                Math.abs(coords.latitude - loc.lat) < 0.05 &&
                Math.abs(coords.longitude - loc.lng) < 0.05;

              return (
                <button
                  key={loc.name}
                  onClick={() => handleSelect(loc.label, loc.area, loc.lat, loc.lng)}
                  className={cn(
                    "flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all hover:bg-secondary/40 active:scale-[0.98]",
                    isSelected
                      ? "border-primary bg-primary/5 text-primary font-medium"
                      : "border-border/50 bg-card text-foreground"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <MapPin className={cn("h-4 w-4", isSelected ? "text-primary" : "text-muted-foreground")} />
                    <div>
                      <p className="font-semibold text-sm">{loc.name}</p>
                      <p className="text-xs text-muted-foreground">{loc.label}</p>
                    </div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
